import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { UserPublicProfile } from '@user/PublicProfile';
import { useUserPublicProfile } from '@user/PublicProfile/useUserPublicProfile';
import { useUserPublicProfiles } from '@user/PublicProfile';
import { Loading } from '@framework/ui/atoms/Loading';
import type { WorkspaceMemberRoles } from '@workspace/domain/workspace';
import { WorkspaceMemberRole } from '@workspace/domain/workspace';
import { RoleSelect } from './RoleSelect';
import { generateDefaultUserIcon } from '@framework/utils';
import type { WorkspaceId, GroupMemberRolesJSON } from 'balus-common';
import { WorkspaceOperation } from '@workspace/domain/WorkspaceOperation';
import { toast } from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';

type Props = {
  workspaceId: WorkspaceId;
  currentUserId: string;
  memberRoles: WorkspaceMemberRoles;
  groupMemberRoles: GroupMemberRolesJSON;
  memberFilter: (name: string) => boolean;
};

export const MemberEditList: React.FC<Props> = ({
  workspaceId,
  currentUserId,
  memberRoles,
  groupMemberRoles,
  memberFilter,
}: Props) => {
  const userIds = useMemo(() => {
    return memberRoles.userIds().filter((id) => id !== currentUserId);
  }, [memberRoles, currentUserId]);
  const myProfile = useUserPublicProfile(currentUserId);
  const myRole = memberRoles.getRoleOf(currentUserId);
  const profiles = useUserPublicProfiles(userIds);

  if (!myProfile || !myRole || !profiles) return <Loading />;

  return (
    <ul className="px-2">
      <MemberItem workspaceId={workspaceId} editable={false} profile={myProfile} role={myRole} isGuest={false} />

      {/* 自分以外 */}
      {profiles.map((profile) => {
        const role = memberRoles.getRoleOf(profile.id);
        if (!role) return null;

        if (!memberFilter(profile.name)) return null;

        return (
          <MemberItem
            key={profile.id}
            workspaceId={workspaceId}
            editable={true}
            profile={profile}
            role={role}
            isGuest={groupMemberRoles[profile.id] === 'guest'}
          />
        );
      })}
    </ul>
  );
};

type ItemProps = {
  workspaceId: WorkspaceId;
  profile: UserPublicProfile;
  role: WorkspaceMemberRole;
  editable: boolean;
  isGuest: boolean;
};

const MemberItem: React.FC<ItemProps> = ({ workspaceId, profile, role, editable, isGuest }: ItemProps) => {
  const { t } = useTranslation();
  const [saved, setSaved] = useState(false);

  const handleChange = useCallback(
    async (role: WorkspaceMemberRole) => {
      try {
        await WorkspaceOperation.updateMemberRole(workspaceId, profile.id, role);
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      } catch (e) {
        toast.error(t('workspace.memberEdit.roleChangeError'));
        console.error(e);
      }
    },
    [profile.id, workspaceId, t]
  );

  return (
    <li className="flex flex-row items-center px-3 py-2 hover:bg-gray-200">
      <span className="flex size-6 overflow-hidden rounded-full">
        {profile.imageUrl ? (
          <img className="size-full object-cover" src={profile.imageUrl} alt={profile.name} />
        ) : (
          <div className="size-full object-cover">
            {generateDefaultUserIcon({
              id: profile.id,
              name: profile.name,
              isAnonymous: profile.isAnonymous,
              size: 24,
            })}
          </div>
        )}
      </span>
      <span className="flex h-6 grow truncate leading-6">
        <span className="mx-1 truncate">{profile.name}</span>
        {isGuest && (
          <span className="mx-1 rounded-lg bg-gray-400 px-2 text-xs">{t('workspace.individualPermission.guest')}</span>
        )}
      </span>

      {editable ? (
        <div className="flex w-30 justify-end">
          {saved && (
            <span className="mr-2 font-bold text-green-700">
              <FontAwesomeIcon icon={faCheck} />
            </span>
          )}
          <RoleSelect role={role} onChange={handleChange} disabled={false} isGuest={isGuest} />
        </div>
      ) : (
        <div className="w-30 text-center">{t(WorkspaceMemberRole.getLocalStringKey(role))}</div>
      )}
    </li>
  );
};
